import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { OrderItemType } from '@mews-commander/data-access';
import { ApplicationState, OrderItem, ProductOrderItem } from '@mews-commander/types';
import { LocalizedText } from '@mews/framework';
import { createHandler } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { getRebateOriginalItem } from '../rebate/selectors/orderItems';
import ProductOrderLabel from '../../../components/commander-ui/ProductOrderLabel';
import SpaceOrderItemTimeUnitLabel from './SpaceOrderItemTimeUnitLabel';

interface WithLastLayer {
    lastLayer?: boolean;
}
interface OrderItemNameStateProps {
    originalOrderItem?: OrderItem;
}
interface OrderItemNameOwnProps {
    orderItem: OrderItem;
}

type OrderItemNameProps = OrderItemNameStateProps & OrderItemNameOwnProps & WithLastLayer;

interface SpaceOrderItemTimeNameOwnProps {
    serviceOrderId: string;
}

const getOrderItemName = createHandler({
    [OrderItemType.CancellationFee]: () => <LocalizedText textKey={TextKey.OrderItemTypeCancellationFee} />,
    [OrderItemType.CityTax]: () => <LocalizedText textKey={TextKey.OrderItemTypeCityTax} />,
    [OrderItemType.CityTaxDiscount]: () => <LocalizedText textKey={TextKey.OrderItemTypeCityTaxDiscount} />,
    [OrderItemType.ExchangeRateDifference]: () => (
        <LocalizedText textKey={TextKey.OrderItemTypeExchangeRateDifference} />
    ),
    [OrderItemType.InvoiceFee]: () => <LocalizedText textKey={TextKey.InvoiceFee} />,
    [OrderItemType.AdditionalExpenseRebate]: ({
        lastLayer,
        originalOrderItem,
    }: WithLastLayer & OrderItemNameStateProps) => {
        if (lastLayer && originalOrderItem) {
            return (
                <div>
                    <OrderItemName orderItem={originalOrderItem} lastLayer={lastLayer} />
                </div>
            );
        }
        return <LocalizedText textKey={TextKey.Rebate} />;
    },
    [OrderItemType.CustomItem]: ({ notes }: OrderItem) => <>{notes}</>,
    [OrderItemType.Deposit]: () => <LocalizedText textKey={TextKey.OrderItemTypeDeposit} />,
    [OrderItemType.NightRebate]: ({ serviceOrderId }: SpaceOrderItemTimeNameOwnProps) => (
        <SpaceOrderItemTimeUnitLabel serviceOrderId={serviceOrderId} />
    ),
    [OrderItemType.ProductOrder]: ({ data }: ProductOrderItem) =>
        data.value.productId !== null ? (
            <ProductOrderLabel productId={data.value.productId} ageCategoryId={data.value.ageCategoryId} />
        ) : null,
    [OrderItemType.ProductOrderRebate]: ({ lastLayer, originalOrderItem }: WithLastLayer & OrderItemNameStateProps) =>
        lastLayer && originalOrderItem ? (
            <div>
                <OrderItemName orderItem={originalOrderItem} lastLayer={lastLayer} />
            </div>
        ) : (
            <LocalizedText textKey={TextKey.Rebate} />
        ),
    [OrderItemType.ServiceCharge]: () => <LocalizedText textKey={TextKey.OrderItemTypeServiceCharge} />,
    [OrderItemType.SpaceOrder]: ({ serviceOrderId }: SpaceOrderItemTimeNameOwnProps) => (
        <SpaceOrderItemTimeUnitLabel serviceOrderId={serviceOrderId} />
    ),
    [OrderItemType.Surcharge]: () => <LocalizedText textKey={TextKey.Surcharge} />,
    [OrderItemType.MulticurrencyFee]: () => <LocalizedText textKey={TextKey.ServiceFee} />,
    [OrderItemType.TaxCorrection]: () => <LocalizedText textKey={TextKey.OrderItemTypeTaxCorrection} />,
    [OrderItemType.AllowanceDiscount]: () => <LocalizedText textKey={TextKey.OrderItemTypeAllowanceDiscount} />,
    [OrderItemType.AllowanceBreakage]: () => <LocalizedText textKey={TextKey.OrderItemTypeAllowanceBreakage} />,
    [OrderItemType.AllowanceContraBreakage]: () => (
        <LocalizedText textKey={TextKey.OrderItemTypeAllowanceContraBreakage} />
    ),
});

const OrderItemName = ({ orderItem, lastLayer, originalOrderItem }: OrderItemNameProps) =>
    getOrderItemName(orderItem.data.discriminator, { ...orderItem, lastLayer, originalOrderItem });

const connectOrderItemName = connect<OrderItemNameStateProps, {}, OrderItemNameOwnProps, ApplicationState>(
    createStructuredSelector({
        originalOrderItem: getRebateOriginalItem,
    })
)(OrderItemName);

export default connectOrderItemName;
